import {
  ApolloClient,
  ObservableQuery,
  WatchQueryOptions,
  ApolloError,
  ApolloCurrentResult
} from 'apollo-client';
import { action, observable } from 'mobx';
import { Dictionary } from 'src/shared/utility';
import uuidv1 from 'uuid/v1';

const maxUpdateTimeout = 850;
const checkDelay =  100;
const tracker: Dictionary<Tracker> = {};

class Tracker  {
  lastUpdateAppointed: number;
  lastUpdateCalled: number;
  query: ObservableQuery<any>;
  updateTimers: any[];
  latestValue: any;
  updateFunction: any;

  constructor(query: ObservableQuery<any>, updateFunction: any) {
    this.query = query;
    const currentTime = Date.now();
    this.lastUpdateAppointed = currentTime;
    this.lastUpdateCalled = currentTime;
    this.updateFunction = updateFunction;
    this.updateTimers = [];
  }

  destroy() {
    if (this.updateTimers.length > 0) {
      this.updateTimers.forEach(timer => window.clearTimeout(timer));
    }
  }

  update(...args: any[]) {
    const newTime = Date.now();
    
    this.lastUpdateAppointed = newTime;
    this.latestValue = args;
    
    if (newTime - this.lastUpdateCalled > maxUpdateTimeout) {
      this._update(newTime, args);
    } else {
      window.setTimeout(
        (me, _newTime, _args) => {
          if (me.lastUpdateAppointed === _newTime) {
            me._update(_newTime, _args);        
          }
        }, 
        checkDelay,
        this,
        newTime,
        args
      );
      
    }
  }

  private _update(updateTime: number, args: any) {
    this.lastUpdateCalled = updateTime;
    this.updateFunction(...args);
  }
}

export interface MobxApolloQueryOptions<T> extends WatchQueryOptions {
  client: ApolloClient<any>;
  onFirstLoad?: (error: any, result?: any) => void;
  onFetch?: (result: T) => void;
  onError?: (error: ApolloError) => void;
}

export interface MobxApolloQuery<T> {
  loading: boolean;
  data?: T;
  error?: ApolloError;
  ref: ObservableQuery<T>;
  forceUpdate: () => void;
  update: (variables: any) => void;
}

type CustomResult<T> = ApolloCurrentResult<T> & Partial<{
  firstLoaded: Boolean;
}>;

export default function graphql<T> (
  options: MobxApolloQueryOptions<T>
): MobxApolloQuery<T> {
  const uuid = uuidv1();
  const { client, onError, onFetch, onFirstLoad, ...opts } = options;
  let result;
  const query = client.watchQuery(opts);
  result = query.currentResult();
  const observableQuery: CustomResult<any> = observable(result);
  
  observableQuery.firstLoaded = false;
  // let lastUpdate = Date.now();
  
  const updateObservable = (value: any) => {
    observableQuery.error = undefined;
    observableQuery.loading = value.loading;
    observableQuery.data = value.data;

    // console.debug(`updating ${Date.now() - lastUpdate} ${uuid}`);
    // lastUpdate = Date.now();
  };

  tracker[uuid] = new Tracker(query, updateObservable);
  
  query.subscribe({
    next: action((value: any) => {
      if (!observableQuery.firstLoaded) {
        observableQuery.firstLoaded  = true;
        if (onFirstLoad) {
          onFirstLoad(null, observableQuery);
        }
      }
      tracker[uuid].update(value);
      if (onFetch) {
        onFetch(value.data);
      } 
    }),
    error: action((error: ApolloError) => {
      observableQuery.error = error;
      observableQuery.loading = false;
      observableQuery.data = undefined;

      if (onError) {
        onError(error);
      }

      if (!observableQuery.firstLoaded) {
        observableQuery.firstLoaded  = true;
        
        if (onFirstLoad) {
          onFirstLoad(null, observableQuery);
        }
      }
    })
  });
  
  return Object.assign(observableQuery, { 
    ref: query,
    getResult: () => result,
    update: action((newData: any) => {
      // client.writeQuery({
      //   query: opts.query,
      //   variables: opts.variables,
      //   data: newData
      // });
      result = newData;
      observableQuery.data = newData;
    })
  }) as any;
}



// WEBPACK FOOTER //
// ./src/shared/mobx-apollo.ts